import {
  audioManager,
  CustomEvents,
  corePhasesManager,
  playersManager,
  gsap,
  modes,
  fpsManager,
  type PlayerInfo,
  type TournamentDataFromResultsRequest
} from '@powerplay/core-minigames'
import {
  type DisciplinePhaseManager,
  AudioNames,
  DisciplinePhases
} from '../types'
import { StartPhaseManager } from './StartPhase/StartPhase'
import { FinishPhaseManager } from './FinishPhase/FinishPhase'
import { player } from '../entities/player'
import { endManager } from '../EndManager'
import { DrivePhaseManager } from './DrivePhase/DrivePhase'
import { StartUpPhaseManager } from './StartUpPhase/StartUpPhase'
import { audioHelper } from '../audio/AudioHelper'
import { pushbarManager } from '../PushbarManager'
import { tutorialFlow } from '../modes/tutorial/TutorialFlow'
import { triggersManager } from '../entities/trigger/TriggersManager'
import { hill } from '../entities/hill/Hill'
import { trainingTasks } from '../modes/training'
import {
  pushBarState,
  tableState,
  trainingResultsState,
  tutorialState
} from '@/stores'
import { stateManager } from '../StateManager'
import { waitingState } from '@powerplay/core-minigames-ui'

/**
 * Trieda pre spravu faz
 */
export class DisciplinePhasesManager {

  /** aktualna faza */
  public actualPhase = 0

  /** pole vytvorenych faze managerov */
  private phaseManagers: DisciplinePhaseManager[] = []

  /** tween na nastartovanie fazoveho managera */
  private startDisciplineTween!: gsap.core.Tween

  /** ci bola hra ukoncena predcasne */
  public prematureEnded = false

  /** Flag na zistenie ci je nastavena */
  public tournamentDataSet = false

  /**
   * Vytvorenie a nastavenie veci
   */
  public create(): void {

    this.createAllPhases()

  }

  /**
   * Vytvorenie menegerov faz
   */
  public createAllPhases(): void {

    this.phaseManagers[DisciplinePhases.start] = new StartPhaseManager(() => {

      this.startDisciplinePhase(DisciplinePhases.startUp)

    })

    this.phaseManagers[DisciplinePhases.startUp] = new StartUpPhaseManager(() => {

      this.startDisciplinePhase(DisciplinePhases.drive)

    })

    this.phaseManagers[DisciplinePhases.drive] = new DrivePhaseManager(() => {

      this.startDisciplinePhase(DisciplinePhases.finish)

    })

    this.phaseManagers[DisciplinePhases.finish] = new FinishPhaseManager(() => {

      this.actualPhase += 1
      console.log('dispatch end')
      if (DisciplinePhases[this.actualPhase]) {

        console.log('dispatch end')
        window.dispatchEvent(new CustomEvent(CustomEvents.finishDisciplinePhase))
        waitingState().isWaiting = true

      }

    })

  }

  /**
   * Zistenie, ci jedna z faza je aktualna faza
   * @param phase - Pole faz na skontrolovanie
   * @returns True, ak je jedna z faz aktualna
   */
  public oneOfPhaseIsActual(phases: DisciplinePhases[]): boolean {

    return phases.includes(this.actualPhase)

  }

  public getActualPhase(): DisciplinePhases {

    return this.actualPhase

  }

  /**
   * Vratenie konkretneho fazoveho menezera
   * @param phase - Faza
   * @returns Fazovy menezer
   */
  public getDisciplinePhaseManager(phase: DisciplinePhases): DisciplinePhaseManager {

    return this.phaseManagers[phase]

  }

  /**
   * Spustenie fazy
   * @param phase - Cislo fazy
   */
  public startDisciplinePhase(phase: DisciplinePhases): void {

    this.actualPhase = phase
    this.phaseManagers[phase].startPhase()

  }

  /**
   * Update aktualnej fazy kazdy frame
   */
  public update(): void {

    this.phaseManagers[this.actualPhase]?.update()

    audioHelper.update()

  }

  /**
   * resetovanie hry
   */
  public resetAttempt(): void {

    player.reset()

    this.createAllPhases()
    pushbarManager.reset()
    pushBarState().$reset()
    if (modes.isTutorial()) {

      tutorialState().$reset()
      tutorialFlow.setObjectivesInit()
      const startPhase = disciplinePhasesManager
        .getDisciplinePhaseManager(DisciplinePhases.start) as StartPhaseManager
      startPhase.isResetAttempt = true

    }
    this.actualPhase = 0
    this.startDisciplinePhase(DisciplinePhases.start)

  }

  /**
   * rekurzivne ukoncime vsetky fazy
   */
  public disciplinePrematureEnd = async (): Promise<void> => {

    audioManager.stopAllAudio()
    audioManager.play(AudioNames.audienceNoise, undefined, undefined, 1)

    // musime vymazat vsetky aktivne fazy
    this.actualPhase = DisciplinePhases.end

    corePhasesManager.disciplineActualAttempt = corePhasesManager.disciplineAttemptsCount

    playersManager.setPlayerResultsDNF()

    if (!modes.isTutorial()) {

      playersManager.setStandings(3)
      console.log('STANDINGS', playersManager.getStandings())

    }

    fpsManager.pauseCounting()

    // posleme udaje
    endManager.sendLogEnd()
    endManager.sendSaveResult()

    // reset states
    stateManager.resetPinia(modes.isTournament() ? ['waitingState', 'blurState', 'tableState'] : [])

    waitingState().isWaiting = true
    tableState().$patch({
      showTable: true,
      activeState: false,
      dataTable: [],
      isStartList: false,
    })
    trainingResultsState().isDisabledPlayAgain = true
    if (modes.isTrainingMode()) {

      trainingTasks.setPrematureEndPenalisation()

    }
    // stopneme vsetky animacne callbacky
    player.animationsManager.removeCallbacksFromAllAnimations()

  }

  /**
   * Nastartovanie disciplinoveho fazoveho managera
   */
  public setStartPhase = (): void => {

    // musime tu dat mensi delay, lebo mozeme skipovat este nejake fazy predtym
    this.startDisciplineTween = gsap.to({}, {
      duration: modes.isTutorial() ? 0 : 0.2,
      onComplete: () => {

        this.startDisciplinePhase(DisciplinePhases.start)

      }
    })

  }

  /**
   * Nastavenie dat o hracoch
   * @param dataCallback - funkcia na ukladanie
   */
  public setOpponentsForFinishTable(dataCallback: TournamentDataFromResultsRequest): void {

    const newPlayersData: PlayerInfo[] = []
    let index = 0
    dataCallback.players.forEach((playerInfo) => {

      playerInfo.attribute = {
        base: 0,
        total: 0
      }

      if (playerInfo.uuid === player.uuid) playerInfo.playable = true

      // musime zarucit, ze hrac bude aj tu prvy
      if (!playerInfo.playable) index += 1
      newPlayersData[playerInfo.playable ? 0 : index] = playerInfo

    })

    playersManager.players = newPlayersData

    // musime este upravit data pre hraca, kedze sa vyssim priradenim dali na DNF
    playersManager.players[0].resultsArr = playersManager.players[0].resultsArrOriginal

    this.tournamentDataSet = true

    waitingState().isWaiting = false
    tableState().activeState = true

    window.dispatchEvent(new CustomEvent(CustomEvents.startFinalStandingsPhase))

  }

  public reset(): void {

    this.createAllPhases()
    pushBarState().$reset()
    this.actualPhase = 0
    pushbarManager.reset()
    triggersManager.reset()
    hill.reset()
    this.prematureEnded = false
    player.reset()

  }

}

export const disciplinePhasesManager = new DisciplinePhasesManager()
